<script setup lang="ts">
/**
 * Manuscript main pane: prose editor, or readonly map figure preview (#266).
 *
 * EditorShell holds the routed view as its editor (`ref="editor"`), so this
 * pane re-exposes the inner DocumentEditor's EditorPort (#298 / #299).
 * Every command is a no-op while the map preview shows.
 */
import { computed, useTemplateRef } from "vue";
import { storeToRefs } from "pinia";
import { isJust, isNothing, nothing, toMaybe, type Maybe } from "../lib/maybe";
import type { EditorPort, EditorPortHandle } from "../editor/port";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import DocumentEditor from "./DocumentEditor.vue";
import ManuscriptMapPreview from "./ManuscriptMapPreview.vue";

const session = useEditorSessionStore();
const { manuscript, activePath, rootPath } = storeToRefs(session);

const activeMapId = computed<Maybe<string>>(() => {
  if (isNothing(activePath.value) || isNothing(rootPath.value)) return nothing;
  const active = activePath.value.value.replace(/\\/g, "/");
  const root = rootPath.value.value.replace(/\\/g, "/").replace(/\/$/, "");
  for (const item of manuscript.value) {
    if (item.type !== "map") continue;
    const mapAbs = `${root}/${item.mapPath}`.replace(/\\/g, "/");
    if (active === mapAbs || active.endsWith(`/${item.mapPath}`)) {
      return { tag: "just" as const, value: item.id };
    }
  }
  return nothing;
});

const doc = useTemplateRef<EditorPortHandle>("doc");

/** Call `name` on the inner editor when it is mounted and exposes it. */
function forward<K extends keyof EditorPort>(name: K): EditorPort[K] {
  const call = (...args: Parameters<EditorPort[K]>): void => {
    const editor = toMaybe(doc.value);
    if (isNothing(editor)) return;
    const method = toMaybe(editor.value[name]);
    if (isNothing(method)) return;
    (method.value as (...rest: Parameters<EditorPort[K]>) => void)(...args);
  };
  return call as EditorPort[K];
}

/** Typed as the full EditorPort: adding a port method without forwarding it
 * here is a compile error. */
const port: EditorPort = {
  focus: forward("focus"),
  runCommand: forward("runCommand"),
  scrollToComment: forward("scrollToComment"),
  scrollToHeading: forward("scrollToHeading"),
  toggleHistory: forward("toggleHistory"),
  toggleComments: forward("toggleComments"),
  closeHistory: forward("closeHistory"),
  openFind: forward("openFind"),
  findNext: forward("findNext"),
  selectNextOccurrence: forward("selectNextOccurrence"),
  openThesaurus: forward("openThesaurus"),
  ensureCaretVisible: forward("ensureCaretVisible"),
  toggleOutline: forward("toggleOutline"),
  showTrashPreview: forward("showTrashPreview"),
  clearTrashPreview: forward("clearTrashPreview"),
};

defineExpose(port);
</script>

<template>
  <ManuscriptMapPreview
    v-if="isJust(activeMapId)"
    :map-id="activeMapId.value"
  />
  <DocumentEditor v-else ref="doc" />
</template>
